/**
 * AllocationChart - Donut chart showing portfolio allocation by holding.
 *
 * Top 8 holdings shown individually, remainder grouped as "Other".
 * Pure frontend computation from position data — no API call needed.
 */

import { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { TickerLogo } from '@/components/ui/TickerLogo';
import type { Position } from '@/types/portfolio';

interface AllocationChartProps {
  positions: Position[];
}

const COLORS = [
  '#D4A574', // gold (primary accent)
  '#4ade80', // green
  '#60a5fa', // blue
  '#f87171', // red
  '#a78bfa', // purple
  '#fb923c', // orange
  '#2dd4bf', // teal
  '#f472b6', // pink
  '#71717a', // gray (Other)
];

function formatCurrency(value: number): string {
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function formatPct(value: number): string {
  return `${value.toFixed(1)}%`;
}

interface SliceData {
  name: string;
  value: number;
  pct: number;
}

export function AllocationChart({ positions }: AllocationChartProps) {
  const chartData = useMemo(() => {
    // Filter out cash/money market and zero-value positions
    const filtered = positions.filter(
      (p) => p.current_value > 0 && !['SPAXX', 'FDRXX', 'FCASH', 'CORE'].includes(p.symbol.toUpperCase())
    );

    const totalValue = filtered.reduce((sum, p) => sum + p.current_value, 0);
    if (totalValue === 0) return [];

    // Sort by value descending
    const sorted = [...filtered].sort((a, b) => b.current_value - a.current_value);

    const slices: SliceData[] = [];
    const top = sorted.slice(0, 8);
    const rest = sorted.slice(8);

    for (const pos of top) {
      slices.push({
        name: pos.symbol,
        value: pos.current_value,
        pct: (pos.current_value / totalValue) * 100,
      });
    }

    if (rest.length > 0) {
      const otherValue = rest.reduce((sum, p) => sum + p.current_value, 0);
      slices.push({
        name: `Other (${rest.length})`,
        value: otherValue,
        pct: (otherValue / totalValue) * 100,
      });
    }

    return slices;
  }, [positions]);

  const totalValue = useMemo(
    () => positions.filter((p) => !['SPAXX', 'FDRXX', 'FCASH', 'CORE'].includes(p.symbol.toUpperCase()))
      .reduce((sum, p) => sum + p.current_value, 0),
    [positions]
  );

  if (chartData.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-surface-light/30 p-4">
        <h3 className="text-sm font-medium text-text-muted uppercase tracking-wider mb-3">Allocation</h3>
        <p className="text-xs text-text-muted text-center py-8">No position data</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-surface-light/30 p-4">
      <h3 className="text-sm font-medium text-text-muted uppercase tracking-wider mb-2">Allocation</h3>

      <div className="relative">
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={95}
              paddingAngle={2}
              dataKey="value"
              stroke="none"
            >
              {chartData.map((_entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const data = payload[0].payload as SliceData;
                return (
                  <div className="bg-surface-overlay border border-border rounded-lg px-3 py-2 text-xs shadow-lg">
                    <p className="font-semibold text-text-primary">{data.name}</p>
                    <p className="text-text-muted">
                      {formatCurrency(data.value)} ({formatPct(data.pct)})
                    </p>
                  </div>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>

        {/* Center label */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <p className="text-lg font-bold text-text-primary">{formatCurrency(totalValue)}</p>
            <p className="text-[10px] text-text-muted">Invested</p>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
        {chartData.map((slice, i) => (
          <div key={slice.name} className="flex items-center gap-1.5 min-w-0">
            <div
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: COLORS[i % COLORS.length] }}
            />
            {!slice.name.startsWith('Other') && (
              <TickerLogo symbol={slice.name} size="xs" />
            )}
            <span className="text-[10px] text-text-muted truncate">{slice.name}</span>
            <span className="text-[10px] text-text-secondary tabular-nums ml-auto shrink-0">
              {formatPct(slice.pct)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
